import React, { createContext, useState, useEffect, useCallback } from 'react';
import axiosInstance from '../components/axiosInstance';
import { API_URL } from '../utils/apiConfig';

export const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [userRole, setUserRole] = useState(null);
  const [employeeName, setEmployeeName] = useState(() => {
    return localStorage.getItem('employeeName') || null;
  });
  const [employeeId, setEmployeeId] = useState(() => {
    return localStorage.getItem('employeeId') || null;
  });
  const [userStore, setUserStore] = useState(() => {
    return localStorage.getItem('userStore') || null;
  });
  const [userDetails, setUserDetails] = useState(() => {
    const stored = localStorage.getItem('userDetails');
    return stored ? JSON.parse(stored) : null;
  });
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tokenExpiryTime, setTokenExpiryTime] = useState(null);

  const handleLogout = useCallback(() => {
    setIsAuthenticated(false);
    setUserRole(null);
    setEmployeeName(null);
    setEmployeeId(null);
    setUserStore(null);
    setUserDetails(null);
    setError(null);
    setTokenExpiryTime(null);
    localStorage.clear();
    delete axiosInstance.defaults.headers.common['Authorization'];
    
    // Use window.location for navigation instead of useNavigate
    window.location.href = '/login';
  }, []);

  // Parse JWT token and extract expiration time
  const parseJwt = (token) => {
    try {
      const tokenParts = token.split('.');
      if (tokenParts.length !== 3) {
        throw new Error('Invalid token format');
      }
      const payload = JSON.parse(atob(tokenParts[1]));
      return {
        payload,
        expiryTime: payload.exp ? payload.exp * 1000 : null, // Convert to milliseconds
        webauthnExpiry: payload.webauthnExp ? payload.webauthnExp * 1000 : null, // WebAuthN expiry
        lastEmailVerification: payload.lastEmailVerification ? new Date(payload.lastEmailVerification) : null // Last email token verification
      };
    } catch (error) {
      console.error('Error parsing JWT:', error);
      return { payload: {}, expiryTime: null, webauthnExpiry: null, lastEmailVerification: null };
    }
  };

  // Check if token is expired
  const isTokenExpired = useCallback(() => {
    if (!tokenExpiryTime) return true;
    return new Date().getTime() > tokenExpiryTime;
  }, [tokenExpiryTime]);

  // Setup regular token expiration check
  useEffect(() => {
    // Set up token expiration timer
    if (tokenExpiryTime) {
      const timeToExpiry = tokenExpiryTime - new Date().getTime();
      if (timeToExpiry > 0) {
        console.log(`Token will expire in ${Math.floor(timeToExpiry / 1000)} seconds, setting up auto-logout timer`);
        const logoutTimer = setTimeout(() => {
          console.log('Token expired based on expiry time, logging out');
          handleLogout();
        }, timeToExpiry);
        
        return () => clearTimeout(logoutTimer);
      } else {
        // Token is already expired
        console.log('Token is already expired, logging out immediately');
        handleLogout();
      }
    }
  }, [tokenExpiryTime, handleLogout]);

  useEffect(() => {
    const initializeAuth = async () => {
      console.log('Initializing auth from localStorage');
      
      // Check for just-logged-in flag
      const justLoggedIn = localStorage.getItem('justLoggedIn');
      if (justLoggedIn === 'true') {
        console.log('DETECTED DIRECT LOGIN: Just logged in flag found');
        // Clear the flag to prevent issues on future page loads
        localStorage.removeItem('justLoggedIn');
      }
      
      // Clear session expired flag if it exists
      localStorage.removeItem('sessionExpired');
      
      const token = localStorage.getItem('token');
      const storedEmployeeId = localStorage.getItem('employeeId');
      const storedEmployeeName = localStorage.getItem('employeeName');
      const storedUserStore = localStorage.getItem('userStore');
      const storedUserRole = localStorage.getItem('userRole');

      console.log('LocalStorage values:', {
        hasToken: !!token,
        storedEmployeeId,
        storedEmployeeName,
        storedUserStore,
        storedUserRole,
        justLoggedIn
      });

      if (!token) {
        console.log('No token found in localStorage, auth is false');
        setIsLoading(false);
        return;
      }
      
      // Initialize authentication state immediately based on localStorage
      // This provides immediate auth state before async operations
      if (storedUserRole) {
        console.log('Setting initial auth state from localStorage');
        setIsAuthenticated(true);
        setUserRole(storedUserRole);
        
        // If we just logged in, immediately mark loading as complete
        // This bypasses any other async checks and speeds up the process
        if (justLoggedIn === 'true') {
          console.log('DIRECT LOGIN: Immediately completing auth loading');
          setIsLoading(false);
          return; // Skip the rest of the initialization for faster login
        }
      }

      try {
        const { payload, expiryTime, webauthnExpiry, lastEmailVerification } = parseJwt(token);
        console.log('JWT payload decoded:', payload);
        
        // Check if token is expired
        if (expiryTime && new Date().getTime() > expiryTime) {
          console.error('Token expired during initialization');
          handleLogout();
          return;
        }
        
        // Check if WebAuthN is expired but don't logout immediately
        // The user will be prompted to renew on their next login
        if (webauthnExpiry && new Date().getTime() > webauthnExpiry) {
          console.warn('WebAuthN token expired - will require renewal on next login');
        }
        
        // Check if email verification is too old (30 days)
        if (lastEmailVerification) {
          const thirtyDaysAgo = new Date();
          thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
          
          if (lastEmailVerification < thirtyDaysAgo) {
            console.warn('Email verification is older than 30 days - will require new verification on next login');
          }
        }

        const effectiveEmployeeId = payload.employeeId || storedEmployeeId;

        if (effectiveEmployeeId) {
          setEmployeeId(effectiveEmployeeId);
          localStorage.setItem('employeeId', effectiveEmployeeId);
        }

        // Try to get a better name than just the role
        if (!storedEmployeeName || storedEmployeeName === 'null' || storedEmployeeName === 'null null') {
          try {
            // Try user data from localStorage
            const userData = JSON.parse(localStorage.getItem('user') || '{}');
            let betterName = null;
            
            // Check different name format possibilities
            if (userData.name) {
              betterName = userData.name;
            } else if (userData.firstName && userData.lastName) {
              betterName = `${userData.firstName} ${userData.lastName}`;
            } else if (userData.first_name && userData.last_name) {
              betterName = `${userData.first_name} ${userData.last_name}`;
            } else if (userData.email) {
              const emailName = userData.email.split('@')[0];
              betterName = emailName
                .split('.')
                .map(part => part.charAt(0).toUpperCase() + part.slice(1))
                .join(' ');
            }
            
            if (betterName) {
              console.log('Setting better name during init:', betterName);
              localStorage.setItem('employeeName', betterName);
              storedEmployeeName = betterName;
            }
          } catch (err) {
            console.warn('Error getting better name during init:', err);
          }
        }
        
        console.log('Setting authentication state:',  {
          isAuth: true,
          role: payload.role,
          name: storedEmployeeName,
          employeeId: effectiveEmployeeId,
          store: storedUserStore
        });
        
        // Build/refresh userDetails from token payload (supports impersonation)
        try {
          const rebuiltDetails = {
            email: payload.email || (userDetails && userDetails.email) || null,
            role: payload.role || (userDetails && userDetails.role) || null,
            employeeId: effectiveEmployeeId || (userDetails && userDetails.employeeId) || null,
            position: payload.position || (userDetails && userDetails.position) || null,
            locationId: payload.store || (userDetails && userDetails.locationId) || null,
            locationName: (userDetails && userDetails.locationName) || null,
            isImpersonating: !!payload.isImpersonating
          };
          localStorage.setItem('userDetails', JSON.stringify(rebuiltDetails));
          setUserDetails(rebuiltDetails);
        } catch (e) {
          console.warn('Failed to rebuild userDetails from token payload:', e);
        }

        // Set authentication state
        setIsAuthenticated(true);
        setUserRole(payload.role);
        setEmployeeName(storedEmployeeName);
        setUserStore(storedUserStore);
        setTokenExpiryTime(expiryTime);
        
        // Set default headers for axios
        axiosInstance.defaults.headers.common['Authorization'] = `Bearer ${token}`;

        // Set up token expiration timer
        if (expiryTime) {
          const timeToExpiry = expiryTime - new Date().getTime();
          if (timeToExpiry > 0) {
            console.log(`Token will expire in ${Math.floor(timeToExpiry / 1000)} seconds`);
            setTimeout(() => {
              console.log('Token expired, logging out');
              handleLogout();
            }, timeToExpiry);
          }
        }

        console.log('Auth initialized successfully:', {
          role: payload.role,
          employeeId: effectiveEmployeeId,
          expiresAt: expiryTime ? new Date(expiryTime).toLocaleString() : 'unknown'
        });
      } catch (error) {
        console.error('Error during auth initialization:', error);
        handleLogout();
      } finally {
        setIsLoading(false);
      }
    };

    initializeAuth();
  }, [handleLogout]);

  // Unified login function for both token and WebAuthN
  const login = async (token, role, store, employeeId, name = null) => {
    try {
      console.log('Login called with:', { token, role, store, employeeId, name });

      // Parse the token to get expiration time
      const { payload, expiryTime, webauthnExpiry, lastEmailVerification } = parseJwt(token);
      
      // If token is already expired, don't proceed with login
      if (expiryTime && new Date().getTime() > expiryTime) {
        throw new Error('Token has already expired');
      }
      
      // Log WebAuthN expiry status but don't block login
      if (webauthnExpiry) {
        const now = new Date().getTime();
        if (now > webauthnExpiry) {
          console.warn('WebAuthN token expired - will require renewal on next login');
        } else {
          const daysRemaining = Math.floor((webauthnExpiry - now) / (1000 * 60 * 60 * 24));
          console.log(`WebAuthN token valid for ${daysRemaining} more days`);
        }
      }
      
      // Check if email verification is too old (30 days)
      if (lastEmailVerification) {
        const thirtyDaysAgo = new Date();
        thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
        
        if (lastEmailVerification < thirtyDaysAgo) {
          console.warn('Email verification is older than 30 days - will require new verification');
        } else {
          const daysRemaining = Math.floor((lastEmailVerification.getTime() + (30 * 24 * 60 * 60 * 1000) - new Date().getTime()) / (1000 * 60 * 60 * 24));
          console.log(`Email verification valid for ${daysRemaining} more days`);
        }
      }

      // Set up axios headers first
      axiosInstance.defaults.headers.common['Authorization'] = `Bearer ${token}`;

      // Store values in localStorage
      localStorage.setItem('token', token);
      localStorage.setItem('userStore', String(store || ''));
      localStorage.setItem('employeeId', String(employeeId || ''));
      
      // Use role from token if not explicitly provided
      const effectiveRole = role || payload.role;
      const effectiveEmployeeId = employeeId || payload.employeeId;

      // Handle employee name
      if (name) {
        console.log('Setting name from login:', name);
        localStorage.setItem('employeeName', name);
        setEmployeeName(name);
      } else if (effectiveEmployeeId) {
        // Try to fetch employee details to get the name
        try {
           
          // First check if we have the employee data from the response
          if (payload.employee) {
            const employeeData = payload.employee;
            console.log('Using employee data from token payload:', employeeData);
            
            // Use the name field directly if available (per DB schema)
            if (employeeData.name) {
              console.log('Setting name from token payload (name):', employeeData.name);
              localStorage.setItem('employeeName', employeeData.name);
              setEmployeeName(employeeData.name);
              // Exit early since we've set the name
              return;
            } 
            // Fallback to firstName + lastName
            else if (employeeData.firstName && employeeData.lastName) {
              const fullName = `${employeeData.firstName} ${employeeData.lastName}`;
              console.log('Setting name from token payload (firstName+lastName):', fullName);
              localStorage.setItem('employeeName', fullName);
              setEmployeeName(fullName);
              // Exit early since we've set the name
              return;
            }
          }
          
          // If we don't have the name from payload, fetch from API
          console.log('Fetching employee details from API for employeeId:', effectiveEmployeeId);
          
          // Try direct employees endpoint first (most likely to work based on DB schema)
          const employeeResponse = await fetch(`${API_URL}/employees/${effectiveEmployeeId}`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          
          if (employeeResponse.ok) {
            const employeeData = await employeeResponse.json();
            console.log('Fetched employee data from API:', employeeData);
            
            if (employeeData) {
              // Store full user details
              const details = {
                role: effectiveRole,
                employeeId: employeeData.id,
                employeeName: employeeData.name || `${employeeData.firstName} ${employeeData.lastName}`,
                position: employeeData.position,
                locationId: employeeData.locationId,
                locationName: employeeData.locationName || employeeData.store,
                isOwner: employeeData.position && employeeData.position.toLowerCase().includes('owner')
              };
              localStorage.setItem('userDetails', JSON.stringify(details));
              setUserDetails(details);
              
              // Check for name field first (this is the primary field in DB schema)
              if (employeeData.name) {
                console.log('Setting name from employee API data (name):', employeeData.name);
                localStorage.setItem('employeeName', employeeData.name);
                setEmployeeName(employeeData.name);
                return;
              } 
              // If no name field but first and last name exist
              else if (employeeData.firstName && employeeData.lastName) {
                const fullName = `${employeeData.firstName} ${employeeData.lastName}`;
                console.log('Setting name from employee API data (firstName+lastName):', fullName);
                localStorage.setItem('employeeName', fullName);
                setEmployeeName(fullName);
                return;
              }
            }
          } else {
            console.warn('Employee API call failed, trying details endpoint...');
            
            // Fallback to same endpoint if the first one fails
            const detailsResponse = await fetch(`${API_URL}/employees/${effectiveEmployeeId}`, {
              headers: { 'Authorization': `Bearer ${token}` }
            });
            
            if (detailsResponse.ok) {
              const detailsData = await detailsResponse.json();
              console.log('Fetched employee details data:', detailsData);
              
              if (detailsData) {
                // Check for name field first
                if (detailsData.name) {
                  console.log('Setting name from details API (name):', detailsData.name);
                  localStorage.setItem('employeeName', detailsData.name);
                  setEmployeeName(detailsData.name);
                  return;
                } 
                // If no name field but first and last name exist
                else if (detailsData.firstName && detailsData.lastName) {
                  const fullName = `${detailsData.firstName} ${detailsData.lastName}`;
                  console.log('Setting name from details API (firstName+lastName):', fullName);
                  localStorage.setItem('employeeName', fullName);
                  setEmployeeName(fullName);
                  return;
                }
              }
            } else {
              console.warn('Employee details API call also failed');
            }
          }
        } catch (err) {
          console.warn('Failed to fetch employee data for name:', err);
        }
        
        // If we still don't have a name, try to get it from user data in localStorage
        if (!localStorage.getItem('employeeName') || localStorage.getItem('employeeName') === 'null') {
          try {
            // Try to get name from user object in localStorage
            const userData = JSON.parse(localStorage.getItem('user') || '{}');
            console.log('Trying to get name from user object:', userData);
            
            if (userData.name) {
              console.log('Setting name from userData (name):', userData.name);
              localStorage.setItem('employeeName', userData.name);
              setEmployeeName(userData.name);
            } 
            else if (userData.firstName && userData.lastName) {
              const fullName = `${userData.firstName} ${userData.lastName}`;
              console.log('Setting name from userData (firstName+lastName):', fullName);
              localStorage.setItem('employeeName', fullName);
              setEmployeeName(fullName);
            }
            else if (userData.first_name && userData.last_name) {
              const fullName = `${userData.first_name} ${userData.last_name}`;
              console.log('Setting name from userData (first_name+last_name):', fullName);
              localStorage.setItem('employeeName', fullName);
              setEmployeeName(fullName);
            }
            else if (userData.email) {
              // Use email to generate name
              const emailName = userData.email.split('@')[0];
              const formattedName = emailName
                .split('.')
                .map(part => part.charAt(0).toUpperCase() + part.slice(1))
                .join(' ');
              
              console.log('Setting name from email:', formattedName);
              localStorage.setItem('employeeName', formattedName);
              setEmployeeName(formattedName);
            }
          } catch (e) {
            console.error('Error parsing user data for name:', e);
          }
        }
      }

      // Update context state
      setIsAuthenticated(true);
      setUserRole(effectiveRole);
      setEmployeeId(effectiveEmployeeId);
      setUserStore(String(store || ''));
      setTokenExpiryTime(expiryTime);

      // Set up token expiration timer
      if (expiryTime) {
        const timeToExpiry = expiryTime - new Date().getTime();
        if (timeToExpiry > 0) {
          console.log(`Token will expire in ${Math.floor(timeToExpiry / 1000)} seconds`);
          setTimeout(() => {
            console.log('Token expired, logging out');
            handleLogout();
          }, timeToExpiry);
        }
      }

      console.log('Auth context updated:', {
        isAuthenticated: true,
        role: effectiveRole,
        employeeId: effectiveEmployeeId,
        store,
        expiresAt: expiryTime ? new Date(expiryTime).toLocaleString() : 'unknown'
      });

    } catch (e) {
      console.error('Login error:', e);
      setError(e.message);
      handleLogout();
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
      </div>
    );
  }

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated,
        userRole,
        employeeName,
        employeeId,
        userStore,
        userDetails,
        login,
        logout: handleLogout,
        error,
        isTokenExpired,
        tokenExpiryTime
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export default AuthProvider;
